const IMAGE_FORMATS = {
  JPEG: 'jpeg',
  PNG: 'png',
  TIFF: 'tiff',
  HEIC: 'heic',
  WEBP: 'webp',
  UNKNOWN: 'unknown',
};

const WRITE_CAPABILITY_BY_FORMAT = {
  [IMAGE_FORMATS.JPEG]: true,
  [IMAGE_FORMATS.PNG]: false,
  [IMAGE_FORMATS.TIFF]: false,
  [IMAGE_FORMATS.HEIC]: false,
  [IMAGE_FORMATS.WEBP]: false,
  [IMAGE_FORMATS.UNKNOWN]: false,
};

const EXTENSION_TO_FORMAT = {
  jpg: IMAGE_FORMATS.JPEG,
  jpeg: IMAGE_FORMATS.JPEG,
  png: IMAGE_FORMATS.PNG,
  tif: IMAGE_FORMATS.TIFF,
  tiff: IMAGE_FORMATS.TIFF,
  heic: IMAGE_FORMATS.HEIC,
  heif: IMAGE_FORMATS.HEIC,
  webp: IMAGE_FORMATS.WEBP,
};

const MIME_TO_FORMAT = {
  'image/jpeg': IMAGE_FORMATS.JPEG,
  'image/jpg': IMAGE_FORMATS.JPEG,
  'image/png': IMAGE_FORMATS.PNG,
  'image/tiff': IMAGE_FORMATS.TIFF,
  'image/heic': IMAGE_FORMATS.HEIC,
  'image/heif': IMAGE_FORMATS.HEIC,
  'image/webp': IMAGE_FORMATS.WEBP,
};

export const SUPPORTED_FILE_PICKER_ACCEPT = [
  '.jpg',
  '.jpeg',
  '.png',
  '.tif',
  '.tiff',
  '.heic',
  '.heif',
  '.webp',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/heic',
  'image/heif',
  'image/webp',
].join(',');

export function getFormatFromFileName(fileName = '') {
  const normalized = fileName.toLowerCase();
  const extension = normalized.includes('.') ? normalized.split('.').pop() : '';
  return EXTENSION_TO_FORMAT[extension] || IMAGE_FORMATS.UNKNOWN;
}

export function getFormatFromMimeType(mimeType = '') {
  return MIME_TO_FORMAT[mimeType.toLowerCase()] || IMAGE_FORMATS.UNKNOWN;
}

export function getFormatFromSignature(buffer) {
  if (!buffer || buffer.length < 4) {
    return IMAGE_FORMATS.UNKNOWN;
  }

  // JPEG: FF D8 FF
  if (buffer[0] === 0xff && buffer[1] === 0xd8 && buffer[2] === 0xff) {
    return IMAGE_FORMATS.JPEG;
  }

  // PNG: 89 50 4E 47
  if (buffer[0] === 0x89 && buffer[1] === 0x50 && buffer[2] === 0x4e && buffer[3] === 0x47) {
    return IMAGE_FORMATS.PNG;
  }

  // TIFF little-endian: 49 49 2A 00, big-endian: 4D 4D 00 2A
  if (
    (buffer[0] === 0x49 && buffer[1] === 0x49 && buffer[2] === 0x2a && buffer[3] === 0x00) ||
    (buffer[0] === 0x4d && buffer[1] === 0x4d && buffer[2] === 0x00 && buffer[3] === 0x2a)
  ) {
    return IMAGE_FORMATS.TIFF;
  }

  // WebP: "RIFF....WEBP"
  if (
    buffer.length >= 12 &&
    buffer[0] === 0x52 &&
    buffer[1] === 0x49 &&
    buffer[2] === 0x46 &&
    buffer[3] === 0x46 &&
    buffer[8] === 0x57 &&
    buffer[9] === 0x45 &&
    buffer[10] === 0x42 &&
    buffer[11] === 0x50
  ) {
    return IMAGE_FORMATS.WEBP;
  }

  // HEIC/HEIF: ISO BMFF with ftyp and heic/heif compatible brand
  if (buffer.length >= 12) {
    const isFtyp = buffer[4] === 0x66 && buffer[5] === 0x74 && buffer[6] === 0x79 && buffer[7] === 0x70;
    if (isFtyp) {
      const brand = String.fromCharCode(buffer[8], buffer[9], buffer[10], buffer[11]).toLowerCase();
      if (brand.startsWith('hei') || brand.startsWith('mif')) {
        return IMAGE_FORMATS.HEIC;
      }
    }
  }

  return IMAGE_FORMATS.UNKNOWN;
}

export function detectImageFormat({ fileName = '', mimeType = '', buffer } = {}) {
  const byMime = getFormatFromMimeType(mimeType);
  if (byMime !== IMAGE_FORMATS.UNKNOWN) {
    return byMime;
  }

  const byName = getFormatFromFileName(fileName);
  if (byName !== IMAGE_FORMATS.UNKNOWN) {
    return byName;
  }

  return getFormatFromSignature(buffer);
}

export function isMetadataWriteSupported(format) {
  return Boolean(WRITE_CAPABILITY_BY_FORMAT[format]);
}

export function getMetadataWriteCapabilityMatrix() {
  return { ...WRITE_CAPABILITY_BY_FORMAT };
}

export function getMimeTypeForFormat(format) {
  switch (format) {
    case IMAGE_FORMATS.JPEG:
      return 'image/jpeg';
    case IMAGE_FORMATS.PNG:
      return 'image/png';
    case IMAGE_FORMATS.TIFF:
      return 'image/tiff';
    case IMAGE_FORMATS.HEIC:
      return 'image/heic';
    case IMAGE_FORMATS.WEBP:
      return 'image/webp';
    default:
      return 'application/octet-stream';
  }
}

export { IMAGE_FORMATS };
